import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Sun, Loader2, ArrowLeft, KeyRound, Mail } from "lucide-react";
import { formatError } from "@/lib/api";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";

export default function ForgotPassword() {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [otpHint, setOtpHint] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const nav = useNavigate();

  const requestOtp = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true); setOtpHint("");
    try {
      const { data } = await api.post("/auth/forgot-password/email/request", { email: email.trim() });
      toast.success(`OTP request sent (${data.provider})`);
      if (data.dev_mode && data.dev_otp) setOtpHint(`Dev mode OTP: ${data.dev_otp}`);
      setStep(2);
    } catch (ex) {
      setErr(formatError(ex.response?.data?.detail) || "Failed to send OTP");
    } finally { setLoading(false); }
  };

  const doReset = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) { setErr("Passwords don't match"); return; }
    if (newPw.length < 6) { setErr("Password must be 6+ characters"); return; }
    setErr(""); setLoading(true);
    try {
      await api.post("/auth/forgot-password/email/verify", { email: email.trim(), otp: otp.trim(), new_password: newPw });
      setStep(3);
      toast.success("Password updated. You can sign in now.");
    } catch (ex) {
      setErr(formatError(ex.response?.data?.detail) || "Reset failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <Card className="w-full max-w-md border-slate-200 shadow-xl shadow-slate-200/40">
        <CardContent className="p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
              <Sun className="w-5 h-5 text-white" strokeWidth={2.5}/>
            </div>
            <div className="font-bold text-lg" style={{fontFamily:'Outfit'}}>MRM Renewables</div>
          </div>

          <div className="flex items-center gap-2 text-slate-900 mb-1">
            <KeyRound className="w-5 h-5 text-amber-500"/>
            <h2 className="text-xl font-bold" style={{fontFamily:'Outfit'}}>Forgot Password</h2>
          </div>
          <p className="text-sm text-slate-500 mb-5">Reset using your registered email + OTP.</p>

          {step === 1 && (
            <form onSubmit={requestOtp} className="space-y-4" data-testid="forgot-step-1">
              <div>
                <Label htmlFor="fp-email">Registered Email</Label>
                <Input id="fp-email" type="email" value={email} onChange={(e)=>setEmail(e.target.value)}
                  required placeholder="you@example.com" data-testid="fp-email-input" autoFocus/>
                <div className="mt-1 text-[11px] text-slate-500">Hum aap ke registered email par 6-digit OTP bhejenge.</div>
              </div>
              {err && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="fp-error">{err}</div>}
              <Button type="submit" disabled={loading} className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold" data-testid="fp-send-otp-btn">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Sending...</> : <><Mail className="w-4 h-4 mr-2"/>Send OTP</>}
              </Button>
              <Link to="/login" className="inline-flex items-center text-xs text-slate-500 hover:text-slate-800 gap-1" data-testid="fp-back-login"><ArrowLeft className="w-3 h-3"/>Back to sign-in</Link>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={doReset} className="space-y-4" data-testid="forgot-step-2">
              <button type="button" onClick={()=>{setStep(1); setOtp(""); setNewPw(""); setConfirmPw(""); setErr("");}} className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1" data-testid="fp-back-btn"><ArrowLeft className="w-3 h-3"/>Change email</button>
              <div>
                <Label htmlFor="fp-otp">6-digit OTP</Label>
                <Input id="fp-otp" type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
                  value={otp} onChange={(e)=>setOtp(e.target.value.replace(/\D/g,''))} required
                  placeholder="123456" className="tracking-[0.5em] text-center font-mono text-lg"
                  data-testid="fp-otp-input" autoFocus/>
                {otpHint && <div className="mt-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">{otpHint}</div>}
              </div>
              <div>
                <Label htmlFor="fp-new-pw">New Password</Label>
                <Input id="fp-new-pw" type="password" value={newPw} onChange={(e)=>setNewPw(e.target.value)} required placeholder="Min 6 characters" data-testid="fp-new-password-input"/>
              </div>
              <div>
                <Label htmlFor="fp-confirm-pw">Confirm Password</Label>
                <Input id="fp-confirm-pw" type="password" value={confirmPw} onChange={(e)=>setConfirmPw(e.target.value)} required data-testid="fp-confirm-password-input"/>
              </div>
              {err && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="fp-error">{err}</div>}
              <Button type="submit" disabled={loading || otp.length !== 6} className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold" data-testid="fp-reset-btn">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Resetting...</> : "Reset Password"}
              </Button>
            </form>
          )}

          {step === 3 && (
            <div className="text-center py-4" data-testid="forgot-step-3">
              <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-3">
                <KeyRound className="w-7 h-7 text-emerald-600"/>
              </div>
              <div className="text-lg font-bold text-slate-900" style={{fontFamily:'Outfit'}}>Password Updated</div>
              {email && <div className="text-sm text-slate-500 mt-1">Account: <span className="font-mono">{email}</span></div>}
              <Button onClick={()=>nav("/login")} className="mt-4 bg-amber-500 hover:bg-amber-600 text-white font-semibold" data-testid="fp-goto-login-btn">Go to Sign in</Button>
            </div>
          )}
        </CardContent>
      </Card>
      <Toaster position="top-right" richColors/>
    </div>
  );
}
